import { ENTITY_ROLES } from "@hisani/stable";
import { z } from "zod";

const EntityRoleSchema = z.enum(ENTITY_ROLES);

export const JobPostingSchema = z
  .object({
    id: z.string(),
    stableName: z.string(),
    title: z.string(),
    roleSought: EntityRoleSchema.nullable(),
    description: z.string(),
    status: z.enum(["OPEN", "CLOSED"]),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    closedAt: z.coerce.date().nullable(),
    version: z.number().int().positive(),
  })
  .strict();
export type JobPosting = z.infer<typeof JobPostingSchema>;

const PublicJobPostingSchema = JobPostingSchema.pick({
  id: true,
  stableName: true,
  title: true,
  roleSought: true,
  description: true,
  createdAt: true,
});
export type PublicJobPosting = z.infer<typeof PublicJobPostingSchema>;

export const PublicJobPostingDetailSchema = PublicJobPostingSchema.extend({
  status: z.enum(["OPEN", "CLOSED"]),
}).strict();

export const JobPostingBrowseSchema = z.object({
  postings: z.array(PublicJobPostingSchema),
  nextCursor: z.string().nullable(),
});

export const JobPostingListSchema = z.object({
  postings: z.array(JobPostingSchema),
});

const JobApplicationSchema = z
  .object({
    id: z.string(),
    postingId: z.string(),
    postingTitle: z.string(),
    stableName: z.string(),
    applicantDisplayName: z.string().nullable(),
    status: z.enum(["ACTIVE", "WITHDRAWN", "ACCEPTED", "REJECTED"]),
    conversationId: z.string(),
    submittedAt: z.coerce.date(),
    decidedAt: z.coerce.date().nullable(),
    version: z.number().int().positive(),
  })
  .strict();
export type JobApplication = z.infer<typeof JobApplicationSchema>;

export const JobApplicationListSchema = z.object({
  applications: z.array(JobApplicationSchema),
});

const ResumeWorkHistoryEntrySchema = z
  .object({
    stableName: z.string(),
    entityRole: EntityRoleSchema,
    status: z.enum(["ACTIVE", "REMOVED"]),
    joinedAt: z.coerce.date(),
    removedAt: z.coerce.date().nullable(),
  })
  .strict();

export const ResumeSchema = z
  .object({
    bio: z.string().nullable(),
    skills: z.array(z.string()),
    workHistory: z.array(ResumeWorkHistoryEntrySchema),
    updatedAt: z.coerce.date(),
  })
  .strict();

const ResumeSnapshotWorkHistoryEntrySchema = z
  .object({
    stableName: z.string(),
    entityRole: EntityRoleSchema,
    status: z.enum(["ACTIVE", "REMOVED"]),
    joinedAt: z.string(),
    removedAt: z.string().nullable(),
  })
  .strict();

export const ResumeSnapshotSchema = z
  .object({
    applicationId: z.string(),
    postingId: z.string(),
    horsemanDisplayName: z.string(),
    bio: z.string().nullable(),
    skills: z.array(z.string()),
    workHistory: z.array(ResumeSnapshotWorkHistoryEntrySchema),
    submittedAt: z.string(),
  })
  .strict();

export const HireMessageSchema = z
  .object({
    id: z.string(),
    senderHorsemanId: z.string(),
    senderRole: z.enum(["APPLICANT", "STABLE"]),
    body: z.string(),
    createdAt: z.coerce.date(),
  })
  .strict();

export const HireConversationSchema = z
  .object({
    id: z.string(),
    applicationId: z.string(),
    postingId: z.string(),
    postingTitle: z.string(),
    stableName: z.string(),
    applicantDisplayName: z.string().nullable(),
    viewerRole: z.enum(["APPLICANT", "STABLE"]),
    messages: z.array(HireMessageSchema),
    nextCursor: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .strict();
export type HireConversation = z.infer<typeof HireConversationSchema>;
